/**
 * LA FENÊTRE D'ÉCHANGE — ce que le coup qu'on vient de porter a produit, une ligne à la fois.
 *
 * ELLE NE S'OUVRE PAS TOUTE SEULE, et c'est la question qui s'est posée en l'écrivant. La règle
 * absolue du produit est que rien ne surgit pendant qu'on lit ou qu'on réfléchit (design §1). Cette
 * fenêtre-ci s'ouvre PARCE QU'ON A FRAPPÉ : elle est la réponse au clic, pas une alerte. Deux
 * garde-fous pour que la distinction tienne — elle ne demande rien (la réaction se déclare avec
 * l'action, en bas, avant de frapper), et elle se déroule d'un bouton pour qui veut aller vite.
 *
 * ELLE VIT DANS `Partie` ET NON DANS `Combat`. Quand le dernier coup tue, le combat disparaît de la
 * vue et l'écran de mort prend sa place : une fenêtre montée dans le combat s'évanouirait avec lui,
 * juste avant de montrer ce qui a tué. C'est précisément ce qu'on répare.
 *
 * CE QU'ELLE AJOUTE AU RÉCIT DE LA COLONNE : le compte des dégâts subis depuis le début de
 * l'échange. Trois coups à sept sur un héros à vingt points de vie, ce n'est pas trois lignes, c'est
 * une mort qui arrive — et c'est ce qu'on n'avait pas le temps de voir.
 */
import { useEffect, useState } from "react";

import { De } from "../../composants/De.js";
import { Glyphe } from "../../composants/Glyphe.js";
import { Popin } from "../../composants/Popin.js";
import { tempsDe, type Temps } from "../../recit/echange.js";
import type { EvenementCombat } from "../../api/types.js";
import type { Noms } from "../../recit/combat.js";
import { t } from "../../lexique/langue.js";

function Ligne({ temps }: { temps: Temps }) {
  return (
    <li className={`echange__temps echange__temps--${temps.ligne.ton}`}>
      {temps.tour !== null ? <span className="echange__tour capitales">{t("Tour {tour}", { tour: temps.tour })}</span> : null}
      <span className="echange__texte">{temps.ligne.texte}</span>
      {temps.ligne.jet ? (
        <span className="echange__jet">
          <De
            jet={temps.ligne.jet.d20}
            total={temps.ligne.jet.total}
            dd={temps.ligne.jet.dd}
            reussi={temps.ligne.jet.reussi}
            critique={temps.ligne.jet.critique}
            echecCritique={temps.ligne.jet.echecCritique}
          />
        </span>
      ) : null}
    </li>
  );
}

export function FenetreDEchange({
  numero,
  evenements,
  noms,
  surFermeture
}: {
  /** Il change à chaque coup joué. C'est LUI qui ouvre la fenêtre — pas le contenu, qui peut être
   * identique deux tours de suite (« vous manquez », « vous manquez »). */
  numero: number;
  evenements: EvenementCombat[];
  noms: Noms;
  surFermeture: () => void;
}) {
  const [temps, poseLesTemps] = useState<Temps[]>([]);
  const [montres, poseMontres] = useState(0);

  useEffect(() => {
    const suite = tempsDe(evenements, noms);
    poseLesTemps(suite);
    // On montre le premier temps tout de suite : une fenêtre qui s'ouvre vide demande un clic pour
    // dire ce qu'elle a à dire, et ce clic-là n'apprend rien.
    poseMontres(suite.length > 0 ? 1 : 0);
    // `numero` seul en dépendance : les autres changent d'identité à chaque rendu du parent, et la
    // fenêtre se rembobinerait au premier clic sur « Suite ».
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numero]);

  const fini = montres >= temps.length;
  const visibles = temps.slice(0, montres);
  const subis = visibles[visibles.length - 1]?.subis ?? 0;
  const ouvert = temps.length > 0;

  return (
    <Popin
      ouvert={ouvert}
      titre={t("L'échange")}
      glyphe={<Glyphe nom="epee" taille={16} />}
      surFermeture={surFermeture}
    >
      <ol className="echange">
        {visibles.map((t, index) => (
          <Ligne key={index} temps={t} />
        ))}
      </ol>

      {subis > 0 ? (
        <p className="echange__compte">
          <Glyphe nom="vie" taille={14} couleur="var(--sang)" />
          <span>
            {t("Vous encaissez")} <span className="nombre">{subis}</span> {t("dégâts dans cet échange.")}
          </span>
        </p>
      ) : null}

      <div className="echange__pied">
        {fini ? (
          <button type="button" className="bouton bouton--grand" onClick={surFermeture} autoFocus>
            {t("Continuer")}
          </button>
        ) : (
          <>
            <button type="button" className="bouton bouton--grand" onClick={() => poseMontres(montres + 1)} autoFocus>
              {t("Suite")} <Glyphe nom="suite" taille={13} />
            </button>
            {/* POUR QUI EST PRESSÉ. Au douzième combat, on ne veut plus regarder : on veut savoir.
                Sans cette sortie, la fenêtre deviendrait la corvée qu'elle est censée éviter. */}
            <button type="button" className="puce puce--sourde" onClick={() => poseMontres(temps.length)}>
              {t("Tout dérouler")}
            </button>
          </>
        )}
      </div>
    </Popin>
  );
}
